// components/StudentPortal/Home/useStudentHome.js
// Hook de datos del Home "Hoy" (F4). Carga, reutilizando los clientes API
// existentes: el alumno, sus rutas de clase CON escena activa, la meta de la
// escena activa (resuelta via VirtualScene EXPLICITO — regla del audit de
// modelos: nunca populate de escena) y sus tareas. Deriva el "hero" (la accion
// prioritaria) segun: tarea pendiente > escena activa > vacio.
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../../hooks";
import {
  StudentAPI,
  DidacticRoutesAPI,
  MozillaHubRoomsApi,
} from "../../../api";
import { TeacherTasksAPI } from "../../../api/teacherTasksAPI";
import { VirtualScenes } from "../../../api/virtualscene";

const studentApi = new StudentAPI();
const routesApi = new DidacticRoutesAPI();
const tasksApi = new TeacherTasksAPI();
const vsApi = new VirtualScenes();
const hubRoomsApi = new MozillaHubRoomsApi();

const PENDING = ["in_progress", "not_started"];

export function useStudentHome() {
  const { user, accessToken } = useAuth();
  const studentId = user?.user?.student;
  const institutionId = user?.user?.institutions?.[0] || null;

  const [state, setState] = useState({
    loading: true,
    error: null,
    student: null,
    tasks: [],
    tutor: null,
    assistant: null,
    activeRoute: null,
    activeScene: null,
  });

  const load = useCallback(async () => {
    if (!accessToken || !studentId) {
      setState((s) => ({ ...s, loading: false, error: "no-student" }));
      return;
    }
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const studentRes = await studentApi.getStudentsbyID(
        accessToken,
        studentId,
      );
      const student = studentRes?.student || null;
      const classnumber = student?.classnumber;

      // Rutas de la clase con escena activa (mismo criterio que el portal actual)
      let rutasAll = [];
      if (institutionId && classnumber) {
        const r = await routesApi.getRutasDidacticasByClassNumber(
          accessToken,
          institutionId,
          classnumber,
        );
        rutasAll = r?.rutas || [];
      }
      const routes = rutasAll.filter(
        (ru) => ru?.sceneactive && ru?.sceneactive_Id,
      );
      const activeRoute = routes[0] || null;
      const teacher = activeRoute?.teacher || null;
      const tutor = teacher
        ? {
            name: `${teacher.firstname || ""} ${teacher.lastname || ""}`.trim(),
            avatar: teacher.avatar || null,
          }
        : null;

      // Meta de la escena activa: VirtualScene por id (sin populate de escena).
      let activeScene = null;
      if (activeRoute?.sceneactive_Id) {
        try {
          const scene = await vsApi.getVirtualSceneById(
            accessToken,
            activeRoute.sceneactive_Id,
          );
          const m1 = scene?.module_01 || {};
          const banner =
            (typeof m1.bannertop === "object" && m1.bannertop?.url) ||
            m1.bannertopUrl ||
            "";
          activeScene = {
            sceneId: activeRoute.sceneactive_Id,
            title: m1.ModOneTitle || activeRoute.nameRute || "",
            banner,
            enterUrl: activeRoute.sceneactive || "",
            category: Array.isArray(m1.ModOneCategory)
              ? m1.ModOneCategory.join(", ")
              : "",
          };
        } catch (_e) {
          activeScene = {
            sceneId: activeRoute.sceneactive_Id,
            title: activeRoute.nameRute || "",
            banner: "",
            enterUrl: activeRoute.sceneactive || "",
            category: "",
          };
        }
      }

      // Asistente REAL de la ruta (NO "EMMA"). getRouteById popula
      // assistants.assistant (title/miniature). Tomamos el primero disponible.
      let assistant = null;
      const routeForAssistant =
        activeRoute ||
        rutasAll.find((ru) => (ru?.assistants || []).length) ||
        rutasAll[0] ||
        null;
      if (routeForAssistant?._id) {
        try {
          const detail = await routesApi.getRouteById(
            accessToken,
            routeForAssistant._id,
          );
          const route = detail?.route || {};
          const list = (route.assistants || [])
            .map((x) => x && x.assistant)
            .filter(Boolean);
          // Preferimos el asistente ACTIVO de la ruta (assistantActive_Id); si no
          // hay match, caemos al primero asignado.
          const activeId = route.assistantActive_Id
            ? String(route.assistantActive_Id)
            : null;
          const found =
            (activeId && list.find((a) => String(a._id) === activeId)) ||
            list[0] ||
            null;
          if (found) {
            assistant = {
              id: found._id,
              title: found.title,
              miniature: found.miniature,
            };
          }
        } catch (_e) {
          assistant = null;
        }
      }

      // Tareas del alumno (no rompe el home si falla). Enriquecemos cada una
      // con su ESTADO DE PRESENTACION derivado (clave del fix):
      //  - el progressPct cuenta solo PASOS; si estan al 100% pero el examen
      //    requerido NO esta hecho -> estado "exam_pending" (no "en curso 100%").
      let tasks = [];
      try {
        const t = await tasksApi.getStudentTasks(accessToken, studentId);
        const rows = Array.isArray(t?.tasks) ? t.tasks : [];
        tasks = rows
          .filter((row) => row && row.task)
          .map((row) => {
            // El EXAMEN cuenta como una unidad mas del progreso: una tarea con
            // examen requerido no esta al 100% hasta hacerlo. Usamos row.steps
            // (viene en la respuesta) para no depender solo de progressPct.
            const steps = Array.isArray(row.steps) ? row.steps : [];
            const stepsTotal = steps.length;
            const stepsDone = steps.filter((s) => s.completed).length;
            const examRequired = !!(
              row.task?.finalQuizRequired || row.finalQuiz?.required
            );
            const examDone = !!(row.finalQuiz && row.finalQuiz.done);
            const stepsComplete =
              stepsTotal > 0
                ? stepsDone === stepsTotal
                : (Number(row.progressPct) || 0) >= 100;

            const totalUnits = stepsTotal + (examRequired ? 1 : 0);
            const doneUnits = stepsDone + (examRequired && examDone ? 1 : 0);
            const progress =
              totalUnits > 0
                ? Math.round((doneUnits / totalUnits) * 100)
                : Math.round(Number(row.progressPct) || 0);

            let display = "not_started";
            if (examRequired) {
              if (stepsComplete && examDone) display = "completed";
              else if (stepsComplete && !examDone) display = "exam_pending";
              else if (stepsDone > 0) display = "in_progress";
            } else if (stepsComplete) display = "completed";
            else if (stepsDone > 0) display = "in_progress";

            return {
              ...row,
              _progress: progress,
              _examRequired: examRequired,
              _examDone: examDone,
              _display: display,
            };
          });
      } catch (_e) {
        tasks = [];
      }

      setState({
        loading: false,
        error: null,
        student,
        tasks,
        tutor,
        assistant,
        activeRoute,
        activeScene,
      });
    } catch (e) {
      setState((s) => ({ ...s, loading: false, error: e?.message || "error" }));
    }
  }, [accessToken, studentId, institutionId]);

  useEffect(() => {
    load();
  }, [load]);

  // Tarea destacada (la accion de tarea prioritaria), SEPARADA de la sala activa.
  // Son dos cosas distintas: la SALA (activeScene) la abre el profesor en el
  // itinerario y es el bloque principal; las TAREAS son trabajo programado y van
  // como seccion secundaria debajo. Solo cuentan tareas NO completadas
  // (in_progress / exam_pending / not_started).
  const inProgress = state.tasks.find((r) => r._display === "in_progress");
  const examPending = state.tasks.find((r) => r._display === "exam_pending");
  const notStarted = state.tasks.find((r) => r._display === "not_started");

  let featuredTask = null;
  if (inProgress)
    featuredTask = { row: inProgress, kind: "inProgress", cta: "continue" };
  else if (examPending)
    featuredTask = { row: examPending, kind: "examPending" };
  else if (notStarted)
    featuredTask = { row: notStarted, kind: "notStarted", cta: "start" };

  // URL por la que entrar a la escena activa. NO se usa `sceneactive` tal cual:
  // la sala del profesor caduca a las 3h (la cierra el janitor de Hubs) y el
  // alumno veia "esta sala ya no esta disponible" (incidente 2026-11-05). El
  // servidor devuelve la sala si sigue viva o crea una nueva, la misma para
  // toda la clase. Lanza un error con .code ("no-active-scene", "busy"...).
  // Un servidor sin el endpoint (404 sin code) -> la URL guardada, como antes.
  const resolveEnterUrl = useCallback(async () => {
    const route = state.activeRoute;
    const scene = state.activeScene;
    if (!route?._id) return scene?.enterUrl || "";
    try {
      const res = await routesApi.enterActiveScene(accessToken, route._id);
      return res.url;
    } catch (e) {
      if (e?.status === 404 && !e?.code && scene?.enterUrl) {
        return scene.enterUrl;
      }
      throw e;
    }
  }, [accessToken, state.activeRoute, state.activeScene]);

  // Metrica de apertura de la sala activa. El alumno no pasa por
  // /roomshub/load, asi que la apertura solo se ve si la anotamos aqui. Es USO
  // en clase, no ojeo de catalogo.
  const trackSceneOpen = useCallback(
    (url) => {
      const scene = state.activeScene;
      const openedUrl = url || scene?.enterUrl;
      if (!accessToken || !openedUrl) return;
      hubRoomsApi.trackRoomOpen(accessToken, {
        sceneRefId: scene?.sceneId,
        url: openedUrl,
        title: scene?.title,
        source: "student_home",
      });
    },
    [accessToken, state.activeScene],
  );

  return {
    ...state,
    featuredTask,
    pendingStatuses: PENDING,
    reload: load,
    resolveEnterUrl,
    trackSceneOpen,
  };
}
